import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PerformanceData } from '@/lib/types';

// Chart typography constants (Recharts requires numeric fontSize values)
const CHART_FONT = {
  AXIS_TICK: 11,      // text-xs equivalent
  AXIS_LABEL: 13,     // text-sm equivalent
  LEGEND: 11,         // text-xs equivalent
} as const;

interface TrafficPerformanceChartProps {
  data?: PerformanceData[];
  combinedChartData?: PerformanceData[];
  error?: Error | null;
  isLoading: boolean;
  timeRange?: 'daily' | 'weekly';
  campaignName?: string;
  currencySymbol?: string;
}

export const TrafficPerformanceChart = ({ 
  isLoading, 
  error, 
  data, 
  combinedChartData,
  timeRange = 'weekly', 
  campaignName,
}: TrafficPerformanceChartProps) => {
  const [activeLines, setActiveLines] = useState<Set<string>>(
    new Set(['clicks', 'impressions', 'conversions'])
  );



  // Find max values for scaling each metric independently
  const maxValues = useMemo(() => {
    if (!data || data.length === 0) {
      return { impressions: 1, clicks: 1, conversions: 1 };
    }
    
    // Calculate max values, ensuring a minimum of 1 to prevent flat lines at 0
    const values = {
      impressions: Math.max(1, ...data.map(item => item.impressions || 0)),
      clicks: Math.max(1, ...data.map(item => item.clicks || 0)),
      conversions: Math.max(1, ...data.map(item => item.conversions || 0))
    };
    
    return values;
  }, [data]);
  
  // Format Y-axis labels consistently to ensure stable width
  const formatYAxisLabel = (value: number) => {
    // Always format with thousands separators to ensure consistent width
    return value.toLocaleString();
  };
  
  // Format for different metrics
  const formatClicksLabel = (value: number) => {
    return value.toLocaleString();
  };
  
  const formatConversionsLabel = (value: number) => {
    return value.toLocaleString();
  };


  const handleLegendClick = (e: any) => {
    const clickedMetric = e.dataKey;
    setActiveLines((prev) => {
      // If clicking an already isolated metric, show all metrics
      if (prev.size === 1 && prev.has(clickedMetric)) {
        return new Set(['clicks', 'impressions', 'conversions']);
      }
      // Otherwise isolate the clicked metric
      return new Set([clickedMetric]);
    });
  };

  const handleLineClick = (metric: string) => {
    setActiveLines((prev) => {
      // If clicking an already isolated metric, return to all metrics
      if (prev.size === 1 && prev.has(metric)) {
        return new Set(['clicks', 'impressions', 'conversions']);
      }
      // Otherwise isolate the clicked metric
      return new Set([metric]);
    });
  };

  // We're now using a shared Y-axis with the max impressions value as scale

  return (
    <>
      {isLoading && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Traffic Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px] flex items-center justify-center bg-gray-50 rounded-md'>
            <div className='text-center'>
              <Skeleton className='h-8 w-40 mx-auto mb-4' />
              <Skeleton className='h-4 w-64 mx-auto' />
            </div>
          </div>
        </Card>
      )}
      {error && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Traffic Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px] flex items-center justify-center bg-gray-50 rounded-md'>
            <div className='text-center text-gray-500'>
              <p className='text-lg font-medium mb-2'>Error loading performance data</p>
              <p>Please try again later</p>
            </div>
          </div>
        </Card>
      )}
      {!isLoading && !error && (!data || data.length === 0) && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Traffic Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px] flex items-center justify-center bg-gray-50 rounded-md'>
            <div className='text-center text-gray-500'>
              <p className='text-lg font-medium mb-2'>Not enough data</p>
              <p>Performance data will show here when available</p>
            </div>
          </div>
        </Card>
      )}
      {!isLoading && !error && data && data.length > 0 && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Traffic Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <LineChart data={combinedChartData || data}>
                <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                <XAxis
                  dataKey='date'
                  tick={(props) => {
                    const { x, y, payload } = props;
                    // Format date as "DD MMM" (e.g., "27 Apr") for both daily and weekly views
                    const date = new Date(payload.value).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short'
                    });
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text 
                          textAnchor="middle"
                          fill="#374151"
                          fontSize={CHART_FONT.AXIS_TICK}
                          transform="rotate(-75)"
                        >
                          <tspan x={-14} dy="0.2em">{date}</tspan>
                        </text>
                      </g>
                    );
                  }}
                  interval={0}
                  tickMargin={8}
                  padding={{ left: 0, right: 0 }}
                  height={45} // Increase height for angled text with new positioning
                />

                {/* Label Y axis - now shows the active metric for context */}
                <YAxis
                  yAxisId='leftLabel'
                  orientation='left'
                  hide={activeLines.size !== 1}
                  tick={false}
                  axisLine={false}
                  width={0} // No width when showing the label
                  label={{
                    value: activeLines.size === 1 ? 
                      String(Array.from(activeLines)[0]).toUpperCase() : '',
                    angle: -90,
                    position: 'insideLeft',
                    fill: '#6B7280',
                    style: { fontSize: CHART_FONT.AXIS_LABEL, fontWeight: 600 },
                    offset: 10, // Adjust label position inside the main axis area
                  }}
                />

                {/* Y axis for impressions */}
                <YAxis
                  yAxisId='impressions'
                  orientation='left'
                  domain={[0, maxValues.impressions]}
                  tick={{ fill: '#374151', fontSize: CHART_FONT.AXIS_TICK }}
                  tickFormatter={formatYAxisLabel}
                  width={60} // Fixed width ensures consistent spacing
                  hide={activeLines.size !== 1 || !activeLines.has('impressions')}
                />
                
                {/* Y axis for clicks */}
                <YAxis
                  yAxisId='clicks'
                  orientation='left'
                  domain={[0, maxValues.clicks]}
                  tick={{ fill: '#374151', fontSize: CHART_FONT.AXIS_TICK }}
                  tickFormatter={formatClicksLabel}
                  width={60}
                  hide={activeLines.size !== 1 || !activeLines.has('clicks')}
                />
                
                {/* Y axis for conversions */}
                <YAxis
                  yAxisId='conversions'
                  orientation='left'
                  domain={[0, maxValues.conversions]}
                  tick={{ fill: '#374151', fontSize: CHART_FONT.AXIS_TICK }}
                  tickFormatter={formatConversionsLabel}
                  width={60}
                  hide={activeLines.size !== 1 || !activeLines.has('conversions')}
                />

                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value: any, rawName?: string | number) => {
                    const name = rawName !== undefined ? String(rawName) : '';
                    if (!name) return [value, ''];
                    // If this is a previous week value
                    if (name.startsWith('prevWeek')) {
                      return [value.toLocaleString(), `Last Week ${name.replace('prevWeek', '')}`];
                    }

                    return [
                      name === 'conversions'
                        ? Number(value).toFixed(1)
                        : Number(value).toLocaleString(),
                      name.charAt(0).toUpperCase() + name.slice(1)
                    ];
                  }}
                  labelFormatter={(date) =>
                    new Date(date).toLocaleDateString('en-US', 
                      timeRange === 'daily' 
                        ? { weekday: 'short', month: 'short', day: 'numeric' } 
                        : { year: 'numeric', month: 'short', day: 'numeric' }
                    )
                  }
                />

                <Legend
                  wrapperStyle={{ paddingTop: 20, fontSize: CHART_FONT.LEGEND }}
                  formatter={(value) => {
                    if (value.startsWith('prevWeek')) return null;
                    
                    const labels: Record<string, string> = {
                      impressions: 'Impressions',
                      clicks: 'Clicks',
                      conversions: 'Conversions',
                    };
                    return (
                      <span
                        className={`text-sm ${activeLines.has(value) ? 'text-gray-900' : 'text-gray-400'} cursor-pointer`}
                      >
                        {labels[value]}
                      </span>
                    );
                  }}
                  onClick={handleLegendClick}
                />

                {/* Impressions Line */}
                <Line
                  yAxisId='impressions'
                  type='linear'
                  dataKey='impressions'
                  stroke='#EC4899'
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 6,
                    onClick: () => handleLineClick('impressions'),
                    style: { cursor: 'pointer' },
                  }}
                  strokeOpacity={activeLines.has('impressions') ? 1 : 0.2}
                />

                {/* Previous Week Impressions Line (only for daily view) */}
                {timeRange === 'daily' && (
                  <Line
                    yAxisId='impressions'
                    type='linear'
                    dataKey='prevWeekImpressions'
                    stroke='#EC4899'
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    strokeOpacity={activeLines.has('impressions') ? 0.5 : 0.1}
                  />
                )}

                {/* Clicks Line - Use actual click values */}
                <Line
                  yAxisId='clicks'
                  type='linear'
                  dataKey='clicks'
                  name="clicks"
                  stroke='#8B5CF6'
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 6,
                    onClick: () => handleLineClick('clicks'),
                    style: { cursor: 'pointer' },
                  }}
                  strokeOpacity={activeLines.has('clicks') ? 1 : 0.2}
                />

                {/* Previous Week Clicks Line (only for daily view) */}
                {timeRange === 'daily' && (
                  <Line
                    yAxisId='clicks'
                    type='linear'
                    dataKey='prevWeekClicks'
                    name="prevWeekClicks"
                    stroke='#8B5CF6'
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    strokeOpacity={activeLines.has('clicks') ? 0.5 : 0.1}
                  />
                )}

                {/* Conversions Line - Use actual conversion values */}
                <Line
                  yAxisId='conversions'
                  type='linear'
                  dataKey='conversions'
                  name="conversions"
                  stroke='#10B981'
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 6,
                    onClick: () => handleLineClick('conversions'),
                    style: { cursor: 'pointer' },
                  }}
                  strokeOpacity={activeLines.has('conversions') ? 1 : 0.2}
                />

                {/* Previous Week Conversions Line (only for daily view) */}
                {timeRange === 'daily' && (
                  <Line
                    yAxisId='conversions'
                    type='linear'
                    dataKey='prevWeekConversions'
                    name="prevWeekConversions"
                    stroke='#10B981'
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    strokeOpacity={activeLines.has('conversions') ? 0.5 : 0.1}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </>
  );
};